/**
 * PDF @mention and file-comment notifications store projectId, documentId (file), sourceId (annotation id), optional pdfPageNumber.
 * Task mentions always set taskId — PDF/file mentions do not.
 */
export function isPdfHubFilesNotification(n: {
  projectId?: string;
  documentId?: string;
  sourceId?: string;
  taskId?: string;
  opsTaskId?: string;
}): boolean {
  return !!(
    n.projectId &&
    n.documentId &&
    n.sourceId &&
    !n.taskId &&
    !n.opsTaskId
  );
}

/** @deprecated Use isPdfHubFilesNotification — kept for existing call sites. */
export function isPdfHubFilesMentionNotification(n: {
  projectId?: string;
  documentId?: string;
  sourceId?: string;
  taskId?: string;
  opsTaskId?: string;
}): boolean {
  return isPdfHubFilesNotification(n);
}

export function isPdfHubFilesBellNotification(n: {
  type?: string;
  projectId?: string;
  documentId?: string;
  sourceId?: string;
  taskId?: string;
  opsTaskId?: string;
}): boolean {
  if (!isPdfHubFilesNotification(n)) return false;
  const t = n.type || '';
  return (
    t === 'mention' ||
    t === 'task_mention' ||
    t === 'pdf_comment' ||
    t === 'pdf_external_comment'
  );
}

/** Deep link to proposal builder for OPS review notifications. */
export function buildProposalOpsReviewPath(dealId: string): string {
  return `/proposal-builder/${dealId}`;
}

/** Deep link to the cardholder expense categorization inbox. */
export function buildCardExpensesPath(): string {
  return '/card-expenses';
}

/** Matches server `buildPdfCommentAppPath` (Project Hub Files + PDF comment focus). */
export function buildPdfMentionProjectHubPath(params: {
  projectId: string;
  documentId: string;
  annotationId: string;
  pdfPageNumber?: number;
  streamId?: string;
}): string {
  const q = new URLSearchParams();
  q.set('tab', 'files');
  q.set('file', params.documentId);
  q.set('comment', params.annotationId);
  if (params.streamId?.trim()) {
    q.set('track', params.streamId.trim());
  }
  if (params.pdfPageNumber != null && !Number.isNaN(Number(params.pdfPageNumber))) {
    q.set('page', String(params.pdfPageNumber));
  }
  return `/project-hub/${params.projectId}?${q.toString()}`;
}

/** Open Design group comments + plan review summary (matches server `buildPlanReviewAppPath`). */
export function buildPlanReviewSummaryPath(params: {
  projectId: string;
  reviewId: string;
  groupCommentKey?: string;
}): string {
  const q = new URLSearchParams();
  if (params.groupCommentKey?.trim()) {
    q.set('group', params.groupCommentKey.trim());
  }
  q.set('planReview', params.reviewId.trim());
  return `/project-hub/${params.projectId}?${q.toString()}`;
}

/** Client workspace project file. Always pin `?deal=` when known so multi-project leads stay on the intended tab. */
export function buildClientWorkspacePath(
  leadId: string,
  options?: { dealId?: string; comms?: string; step?: string; commentId?: string },
): string {
  const id = leadId.trim();
  if (!id) return '/';
  const q = new URLSearchParams();
  const dealId = options?.dealId?.trim();
  if (dealId) q.set('deal', dealId);
  const comms = options?.comms?.trim();
  if (comms) q.set('comms', comms);
  const commentId = options?.commentId?.trim();
  if (commentId) q.set('comment', commentId);
  const step = options?.step?.trim();
  if (step) q.set('step', step);
  const qs = q.toString();
  return qs ? `/client/${id}?${qs}` : `/client/${id}`;
}

/** Project Hub group-comment thread (`?group=` + optional `taskComment`). */
export function buildProjectHubGroupCommentPath(params: {
  projectId: string;
  groupCommentKey: string;
  commentId?: string;
  discussion?: 'architect' | 'comments' | 'pdf';
}): string {
  const q = new URLSearchParams();
  q.set('group', params.groupCommentKey.trim());
  if (params.commentId?.trim()) q.set('taskComment', params.commentId.trim());
  if (params.discussion === 'architect' || params.discussion === 'comments' || params.discussion === 'pdf') {
    q.set('discussion', params.discussion);
  }
  return `/project-hub/${params.projectId.trim()}?${q.toString()}`;
}

/** Architect portal Design decisions / Architect Discussion. */
export function buildArchitectPortalDiscussionPath(params: {
  projectId: string;
  commentId?: string;
}): string {
  const q = new URLSearchParams();
  q.set('project', params.projectId.trim());
  if (params.commentId?.trim()) q.set('taskComment', params.commentId.trim());
  return `/architect-portal?${q.toString()}`;
}

/** Deep link to a project's staff inspiration board. */
export function buildInspirationBoardPath(leadId: string, dealId: string): string {
  return `/client/${leadId}/project/${dealId}/board`;
}

export function parseInspirationBoardLink(raw: unknown): { leadId: string; dealId: string } | null {
  if (!raw || typeof raw !== 'object') return null;
  const leadId = String((raw as { leadId?: unknown }).leadId || '').trim();
  const dealId = String((raw as { dealId?: unknown }).dealId || '').trim();
  if (!leadId || !dealId) return null;
  return { leadId, dealId };
}

/** Deep link to a staff inspiration board node (pin comment or sticky note). */
export function buildBoardMentionPath(params: {
  leadId: string;
  dealId: string;
  nodeId: string;
}): string {
  const q = new URLSearchParams();
  q.set('node', params.nodeId);
  return `${buildInspirationBoardPath(params.leadId, params.dealId)}?${q.toString()}`;
}

/** Deep link to contractor payables inbox (optional invoice focus). */
export function buildContractorPayablesPath(invoiceId?: string): string {
  if (invoiceId?.trim()) {
    return `/finances/contractor-payables?invoice=${encodeURIComponent(invoiceId.trim())}`;
  }
  return '/finances/contractor-payables';
}

const MARKETING_CONTENT_REQUESTS_PREFIX = '/marketing-hub/content-requests';

/** Marketing content request bell notifications (may reuse task_comment type without projectId). */
export function isMarketingContentRequestNotification(n: {
  link?: string;
  type?: string;
  title?: string;
  projectId?: string;
  opsTaskId?: string;
  taskId?: string;
}): boolean {
  const link = (n.link || '').trim();
  if (link.startsWith(MARKETING_CONTENT_REQUESTS_PREFIX)) return true;
  const title = (n.title || '').toLowerCase();
  return (
    (n.type === 'task_comment' || n.type === 'task_mention') &&
    !n.projectId &&
    !n.opsTaskId &&
    !n.taskId &&
    title.includes('content request')
  );
}

export function buildMarketingContentRequestPath(n: {
  link?: string;
  title?: string;
  sourceId?: string;
}): string {
  const link = (n.link || '').trim();
  if (link.startsWith(MARKETING_CONTENT_REQUESTS_PREFIX)) return link;
  if (n.title === 'New content request' && n.sourceId?.trim()) {
    return `${MARKETING_CONTENT_REQUESTS_PREFIX}?request=${encodeURIComponent(n.sourceId.trim())}`;
  }
  return MARKETING_CONTENT_REQUESTS_PREFIX;
}

/** Build a full Files-tab deep link when a notification carries PDF comment metadata. */
export function buildPdfCommentPathFromNotification(n: {
  projectId?: string;
  documentId?: string;
  sourceId?: string;
  pdfPageNumber?: number;
  streamId?: string;
}): string | null {
  const projectId = n.projectId?.trim();
  const documentId = n.documentId?.trim();
  const annotationId = n.sourceId?.trim();
  if (!projectId || !documentId || !annotationId) return null;
  return buildPdfMentionProjectHubPath({
    projectId,
    documentId,
    annotationId,
    pdfPageNumber: n.pdfPageNumber,
    streamId: n.streamId,
  });
}
